import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';

const PlayerContext = createContext();

export const usePlayer = () => useContext(PlayerContext);

export const PlayerProvider = ({ children }) => {
    const [currentTrack, setCurrentTrack] = useState(null);
    const [currentTrackInfo, setCurrentTrackInfo] = useState({ title: '', artist: '', thumbnail: '' });
    const [isPlaying, setIsPlaying] = useState(false);
    const [playlist, setPlaylist] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [isApiReady, setIsApiReady] = useState(false);
    const [showPlayer, setShowPlayer] = useState(false);
    const [defaultSongs, setDefaultSongs] = useState([]);
    const [pagePlaylist, setPagePlaylist] = useState(null); // Page-specific playlist

    const embedControllerRef = useRef(null);
    const iFrameAPIRef = useRef(null);
    const pendingTrackRef = useRef(null);
    const embedContainerRef = useRef(null);
    const isCreatingRef = useRef(false);
    const playlistRef = useRef([]);
    const currentIndexRef = useRef(-1);
    const songEndHandledRef = useRef(false);
    const handleSongEndRef = useRef(null);

    // Keep refs in sync with state
    useEffect(() => {
        playlistRef.current = playlist;
    }, [playlist]);

    useEffect(() => {
        currentIndexRef.current = currentIndex;
    }, [currentIndex]);

    // Load default songs on mount
    useEffect(() => {
        fetch('/songs.json')
            .then(res => res.json())
            .then(data => setDefaultSongs(data))
            .catch(err => console.error('Error loading songs:', err));
    }, []);

    // Initialize Spotify iFrame API
    useEffect(() => {
        if (window.onSpotifyIframeApiReady) {
            return;
        }

        window.onSpotifyIframeApiReady = (IFrameAPI) => {
            iFrameAPIRef.current = IFrameAPI;
            setIsApiReady(true);
        };
    }, []);



    // Reset player to default state
    const resetPlayer = useCallback(() => {
        setCurrentTrack(null);
        setCurrentTrackInfo({ title: '', artist: '', thumbnail: '' });
        setIsPlaying(false);
        setPlaylist([]);
        setCurrentIndex(-1);
        setShowPlayer(false);
        songEndHandledRef.current = false;
    }, []);

    // Handle when a song ends
    const handleSongEnd = useCallback(() => {
        // Prevent multiple triggers
        if (songEndHandledRef.current) return;
        songEndHandledRef.current = true;

        const pl = playlistRef.current;
        const idx = currentIndexRef.current;

        if (pl.length > 0 && idx < pl.length - 1) {
            // Has next track - advance
            const nextIdx = idx + 1;
            const next = pl[nextIdx];
            setCurrentIndex(nextIdx);
            setCurrentTrack(next.trackId);
            setCurrentTrackInfo({ title: next.title, artist: next.artist, thumbnail: next.thumbnail });
            if (embedControllerRef.current) {
                embedControllerRef.current.loadUri(`spotify:track:${next.trackId}`);
            }
            // Reset flag after a delay to allow next song to start
            setTimeout(() => {
                songEndHandledRef.current = false;
            }, 2000);
        } else {
            // End of playlist - reset to default
            resetPlayer();
        }
    }, [resetPlayer]);

    // Keep handleSongEnd ref in sync
    useEffect(() => {
        handleSongEndRef.current = handleSongEnd;
    }, [handleSongEnd]);

    const createOrLoadEmbed = useCallback((trackId) => {
        if (!iFrameAPIRef.current) {
            pendingTrackRef.current = { trackId };
            return;
        }

        // If controller exists, just load new URI
        if (embedControllerRef.current) {
            embedControllerRef.current.loadUri(`spotify:track:${trackId}`);
            return;
        }

        // Need to create controller - wait for container
        if (!embedContainerRef.current) {
            pendingTrackRef.current = { trackId };
            return;
        }

        // Prevent double creation
        if (isCreatingRef.current) {
            pendingTrackRef.current = { trackId };
            return;
        }

        isCreatingRef.current = true;

        const options = {
            uri: `spotify:track:${trackId}`,
            width: '100%',
            height: 80,
        };

        iFrameAPIRef.current.createController(embedContainerRef.current, options, (controller) => {
            embedControllerRef.current = controller;
            isCreatingRef.current = false;

            let lastPosition = 0;

            controller.addListener('ready', () => {
                controller.play();
                setIsPlaying(true);
            });

            controller.addListener('playback_update', (e) => {
                const { isPaused, position, duration } = e.data;
                setIsPlaying(!isPaused);

                // Detect song end
                // 1. Position is very close to duration (within 1.5s) and paused
                // 2. Or position wrapped to 0 from near end (within 3s)
                const isFinished = isPaused && duration > 0 && (
                    (position >= duration - 1500) ||
                    (position === 0 && lastPosition > duration - 3000)
                );

                if (isFinished) {
                    if (handleSongEndRef.current) {
                        handleSongEndRef.current();
                    }
                }

                lastPosition = position;
            });

            // Handle any track that was queued while creating
            if (pendingTrackRef.current) {
                const pending = pendingTrackRef.current;
                pendingTrackRef.current = null;
                controller.loadUri(`spotify:track:${pending.trackId}`);
            }
        });
    }, []);

    // Handle pending track when API becomes ready
    useEffect(() => {
        if (isApiReady && pendingTrackRef.current && embedContainerRef.current && !isCreatingRef.current) {
            const { trackId } = pendingTrackRef.current;
            pendingTrackRef.current = null;
            createOrLoadEmbed(trackId);
        }
    }, [isApiReady, createOrLoadEmbed]);

    // Callback ref for embed container
    const setEmbedContainer = useCallback((node) => {
        if (!node) return;

        embedContainerRef.current = node;

        // If we have a pending track and API is ready and not currently creating, create embed
        if (pendingTrackRef.current && iFrameAPIRef.current && !isCreatingRef.current && !embedControllerRef.current) {
            const { trackId } = pendingTrackRef.current;
            pendingTrackRef.current = null;
            createOrLoadEmbed(trackId);
        }
    }, [createOrLoadEmbed]);

    const playTrack = useCallback((trackId, trackInfo = {}, newPlaylist = null, index = -1) => {
        setCurrentTrack(trackId);
        setCurrentTrackInfo(trackInfo);
        setShowPlayer(true);

        if (newPlaylist) {
            setPlaylist(newPlaylist);
        }
        if (index >= 0) {
            setCurrentIndex(index);
        }

        // If controller exists, load directly
        if (embedControllerRef.current) {
            embedControllerRef.current.loadUri(`spotify:track:${trackId}`);
        }
        // If API ready and container exists but no controller yet, create it
        else if (iFrameAPIRef.current && embedContainerRef.current && !isCreatingRef.current) {
            createOrLoadEmbed(trackId);
        }
        // Otherwise queue it - the callback ref or useEffect will handle it
        else {
            pendingTrackRef.current = { trackId };
        }
    }, [createOrLoadEmbed]);

    // Play the first song from page playlist or default playlist
    const playFirst = useCallback(() => {
        // Use page-specific playlist if available
        if (pagePlaylist && pagePlaylist.length > 0) {
            const first = pagePlaylist[0];
            setPlaylist(pagePlaylist);
            playTrack(first.trackId, {
                title: first.title,
                artist: first.artist,
                thumbnail: first.thumbnail || ''
            }, pagePlaylist, 0);
        }
        // Otherwise fall back to default songs
        else if (defaultSongs.length > 0) {
            const firstSong = defaultSongs[0];
            const playlistData = defaultSongs.map(s => ({
                trackId: s.id,
                title: s.title,
                artist: s.artist,
                thumbnail: s.thumbnail
            }));
            setPlaylist(playlistData);
            playTrack(firstSong.id, {
                title: firstSong.title,
                artist: firstSong.artist,
                thumbnail: firstSong.thumbnail
            }, playlistData, 0);
        }
    }, [pagePlaylist, defaultSongs, playTrack]);

    const pause = useCallback(() => {
        if (embedControllerRef.current) {
            embedControllerRef.current.pause();
        }
        setIsPlaying(false);
    }, []);

    const resume = useCallback(() => {
        if (embedControllerRef.current) {
            embedControllerRef.current.resume();
        }
        setIsPlaying(true);
    }, []);

    const togglePlay = useCallback(() => {
        if (embedControllerRef.current) {
            embedControllerRef.current.togglePlay();
        }
    }, []);

    const nextTrack = useCallback(() => {
        if (playlist.length > 0 && currentIndex < playlist.length - 1) {
            const nextIdx = currentIndex + 1;
            const next = playlist[nextIdx];
            setCurrentIndex(nextIdx);
            setCurrentTrack(next.trackId);
            setCurrentTrackInfo({ title: next.title, artist: next.artist, thumbnail: next.thumbnail });
            if (embedControllerRef.current) {
                embedControllerRef.current.loadUri(`spotify:track:${next.trackId}`);
            }
        }
    }, [playlist, currentIndex]);

    const prevTrack = useCallback(() => {
        if (playlist.length > 0 && currentIndex > 0) {
            const prevIdx = currentIndex - 1;
            const prev = playlist[prevIdx];
            setCurrentIndex(prevIdx);
            setCurrentTrack(prev.trackId);
            setCurrentTrackInfo({ title: prev.title, artist: prev.artist, thumbnail: prev.thumbnail });
            if (embedControllerRef.current) {
                embedControllerRef.current.loadUri(`spotify:track:${prev.trackId}`);
            }
        }
    }, [playlist, currentIndex]);

    // Check if controls should be enabled (has playlist loaded)
    const hasPlaylist = playlist.length > 0 || defaultSongs.length > 0;

    const value = {
        currentTrack,
        currentTrackInfo,
        isPlaying,
        playlist,
        currentIndex,
        isApiReady,
        showPlayer,
        hasPlaylist,
        setEmbedContainer,
        playTrack,
        playFirst,
        pause,
        resume,
        togglePlay,
        nextTrack,
        prevTrack,
        spotifyApi: iFrameAPIRef.current,
        setPlaylist,
        setPagePlaylist,
    };

    return (
        <PlayerContext.Provider value={value}>
            {children}
        </PlayerContext.Provider>
    );
};
